import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { doc, getDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { LuCheck, LuClipboardList, LuDownload, LuX } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import Modal from '../../../components/ui/Modal';
import { db } from '../../../config/firebaseConfig';
import { useTranslation } from '../../../i18n/LanguageContext';
import { BLOCK_TITLE } from '../components/ProfileUi';
import { TODO_LABELS, formatTimestamp } from './visitLabels';

const SECTION = 'tw-space-y-3 tw-rounded-[1.25rem] tw-border tw-border-slate-200/80 tw-bg-slate-50/60 tw-p-4 dark:tw-border-white/10 dark:tw-bg-white/[0.02]';

/**
 * Tamamlanmış bir visitin tüm ayrıntıları (vizit tipine göre alanlar,
 * yapılacaklar listesi) ve tek satırlık Excel dökümü.
 */
const VisitDetailsDialog = ({ show, onHide, visit }) => {
  const { t, language, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.projectVisits.${key}`, vars);
  const [craName, setCraName] = useState("");
  const [projectTitle, setProjectTitle] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      if (visit.selectedCra) {
        try {
          const craDoc = await getDoc(doc(db, 'ExternalPersonnels', visit.selectedCra));
          if (craDoc.exists()) {
            const craData = craDoc.data();
            setCraName(`${craData['Adı'] || ''} ${craData['Soyadı'] || ''}`.trim());
          }
        } catch (error) {
          console.error('Error fetching CRA details:', error);
          setCraName("Error loading CRA name");
        }
      }
      if (visit.projectId) {
        try {
          const projectDoc = await getDoc(doc(db, 'Projects', visit.projectId));
          if (projectDoc.exists()) {
            const pd = projectDoc.data();
            setProjectTitle(pd.titleShort || pd.title || pd.name || '');
          }
        } catch (error) {
          console.error('Error fetching project details:', error);
        }
      }
    };
    fetchData();
  }, [visit.selectedCra, visit.projectId]);

  const ev = visit.visitEvaluation || {};

  const val = (v) => v || '-';

  const formatBool = (v) => {
    if (v === true || v === 'yes' || v === 'evet') return 'Evet';
    if (v === false || v === 'no' || v === 'hayır') return 'Hayır';
    return v || '-';
  };

  const computeCycleTime = () => {
    if (ev.cycleTime) return ev.cycleTime;
    const startDate = visit.date;
    const endDate = ev.processEndDate;
    if (startDate && endDate) {
      const diffMs = new Date(endDate) - new Date(startDate);
      if (!isNaN(diffMs) && diffMs >= 0) return Math.round(diffMs / 86400000);
    }
    return '-';
  };

  const computeVisitDuration = () => {
    if (ev.visitDurationHours !== undefined) {
      return { hours: val(String(ev.visitDurationHours)), minutes: val(String(ev.visitDurationMinutes)) };
    }
    const s = visit.startTime, e = visit.endTime;
    if (s && e) {
      const [sh, sm] = s.split(':').map(Number);
      const [eh, em] = e.split(':').map(Number);
      const total = (eh * 60 + em) - (sh * 60 + sm);
      if (total >= 0) return { hours: String(Math.floor(total / 60)), minutes: String(total % 60) };
    }
    return { hours: '-', minutes: '-' };
  };

  const downloadExcel = () => {
    const ev = visit.visitEvaluation || {};
    // Create basic information object
    const basicInfo = {
      'Tarih': visit.date || "-",
      'Visit Tipi': visit.visitType || "-",
      'Başlangıç Saati': visit.startTime || "-",
      'Bitiş Saati': visit.endTime || "-",
      'Hasta ID': visit.patientId || "-",
      'Doktor': visit.doctor || "-",
      'Hastane': visit.hospital || "-",
      'Şehir': visit.city || "-",
      'Seyahat Tipi': visit.travelType || "-",
      'Durum': visit.status ? "Tamamlandı" : "Devam Ediyor",
      'Kaynak Döküman': visit.sourceDocument || "-",
      'Kaynak Döküman Tamamlanma Saati': visit.sourceDocumentCompletionTime || "-",
      'Bilgi': visit.information || "-",
      'Oluşturulma Tarihi': formatTimestamp(visit.createdAt),
      'Son Güncelleme': visit.lastUpdated || "-",
      'Tamamlanma Tarihi': visit.completedAt || "-",
      'İlk Visit Tarihi': visit.firstVisitDate || "-",
      'Çalışan Adı': visit.employeeName || "-",
      'Seçilen CRA': craName || "-",
      'Periyot': visit.period || "-",
      'Opsiyon': visit.option || "-",
      // visitEvaluation fields
      'eCRF Tamamlanma Tarihi': ev.ecrfCompletionDate || "-",
      'eCRF Tamamlanma Saati': ev.ecrfCompletionTime || "-",
      'Kaynak Dok. Tamamlanma Tarihi': ev.sourceDocCompletionDate || "-",
      'Kaynak Dok. Tamamlanma Saati': ev.sourceDocCompletionTime || "-",
      'İmza Tamamlanma Tarihi': ev.signatureCompletionDate || "-",
      'İmza Tamamlanma Saati': ev.signatureCompletionTime || "-",
      'Süreç Bitiş Tarihi': ev.processEndDate || "-",
      'Protokol Sapması': ev.protocolDeviation ? "Evet" : "Hayır",
      'CAPA Açıldı mı?': ev.capaOpened ? "Evet" : "Hayır",
      'Sorun Oluştu mu?': ev.issueOccurred ? "Evet" : "Hayır",
      'SLA Hedefi (gün)': ev.slaTarget || "-",
    };

    // Create combined todo list object including both regular and pre-visit items
    const todoLabels = TODO_LABELS;

    const todoListData = Object.entries(visit.todoList || {}).reduce((acc, [key, value]) => {
      const label = todoLabels[key] || key;
      const prefix = key.startsWith('pre_') ? 'Ön Ziyaret - ' : 'Yapılacak - ';
      acc[`${prefix}${label}`] = value ? "✓" : "✗";
      return acc;
    }, {});

    // Combine all data
    const dataToExport = [{ ...basicInfo, ...todoListData }];

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Visit Detayları');
    XLSX.writeFile(workbook, `Visit_Detay_${visit.date || 'Tarihsiz'}.xlsx`);
  };

  const isHastane = visit.visitType === 'hastane';
  const isHome = visit.visitType === 'home';

  // Evet/Hayır değerleri ekranda renkli rozetle gösterilir
  const yesNo = (value) => {
    const text = formatBool(value);
    if (text === 'Evet') return <Badge tone="success">{t('visitModal.common.yes')}</Badge>;
    if (text === 'Hayır') return <Badge tone="neutral">{t('visitModal.common.no')}</Badge>;
    return text;
  };
  const coordinator = val(visit.siteCoordinator || craName || visit.employeeName);
  const typeText = visit.visitType ? t(`profilePage.calendar.visitTypes.${visit.visitType}`) : '-';
  const visitTypeLabel = typeText.startsWith('profilePage.') ? visit.visitType : typeText;
  const statusText = visit.status ? label('status.done') : label('status.ongoing');

  const sections = [];
  if (isHastane) {
    sections.push({
      key: 'hospital',
      rows: [
        ['projectName', val(projectTitle)],
        ['centerId', val(visit.hospital)],
        ['visitId', val(visit.visitNumber || visit.id)],
        ['visitType', visitTypeLabel],
        ['siteCoordinator', coordinator],
        ['patientId', val(visit.patientId)],
        ['screenFail', yesNo(ev.screenFail)],
        ['dropPatient', yesNo(ev.dropPatient)],
        ['visitDate', val(visit.date)],
        ['visitStartTime', val(visit.startTime)],
        ['protocolDeviation', yesNo(ev.protocolDeviation)],
        ['capaOpened', yesNo(ev.capaOpened)],
        ['comments', val(visit.information), true],
        ['ecrfDate', val(ev.ecrfCompletionDate)],
        ['ecrfTime', val(ev.ecrfCompletionTime)],
        ['sourceDocDate', val(ev.sourceDocCompletionDate)],
        ['sourceDocTime', val(ev.sourceDocCompletionTime)],
        ['signatureDate', val(ev.signatureCompletionDate)],
        ['signatureTime', val(ev.signatureCompletionTime)],
        ['processEndDate', val(ev.processEndDate)],
        ['cycleTime', computeCycleTime()],
        ['issueOccurred', yesNo(ev.issueOccurred)],
        ['slaTarget', val(ev.slaTarget)],
        ['slaMet', yesNo(ev.slaMet)],
        ['slaOverdue', yesNo(ev.slaOverdue)],
      ],
    });
  }
  if (isHome) {
    const duration = computeVisitDuration();
    sections.push({
      key: 'home',
      rows: [
        ['protocolNo', val(ev.protocolNo || projectTitle)],
        ['siteId', val(visit.hospital)],
        ['patientId', val(visit.patientId)],
        ['visitId', val(visit.visitNumber || visit.id)],
        ['windowOpen', val(ev.windowOpenDate)],
        ['targetDate', val(ev.targetDate)],
        ['windowClose', val(ev.windowCloseDate)],
        ['siteCoordinator', coordinator],
        ['subjectStatus', val(ev.subjectStatus)],
        ['visitStartDate', val(visit.date)],
        ['visitStartTime', val(visit.startTime)],
        ['visitEndDate', val(ev.visitEndDate || visit.date)],
        ['visitEndTime', val(visit.endTime)],
        ['durationHours', duration.hours],
        ['durationMinutes', duration.minutes],
        ['ecrfDate', val(ev.ecrfCompletionDate)],
        ['ecrfTime', val(ev.ecrfCompletionTime)],
        ['sourceDocDate', val(ev.sourceDocCompletionDate)],
        ['sourceDocTime', val(ev.sourceDocCompletionTime)],
        ['signatureDate', val(ev.signatureCompletionDate)],
        ['signatureTime', val(ev.signatureCompletionTime)],
        ['processEndDate', val(ev.processEndDate)],
        ['cycleTime', computeCycleTime()],
      ],
    }, {
      key: 'issues',
      rows: [
        ['issueDuringVisit', yesNo(ev.issueOccurred)],
        ['issueDeviation', yesNo(ev.protocolDeviation)],
        ['issueSource', val(ev.issueDetails || ev.issueCategory), true],
        ['smoCorrections', val(ev.smoCorrectionRequests), true],
        ['qualityCheck', val(ev.qualityManagerCheck)],
        ['craRequest', val(ev.craRequest)],
        ['correctionReason', val(ev.correctionReason), true],
        ['initialSource', val(ev.dateInitialSourceReceived)],
        ['latestRevision', val(ev.dateLatestSourceRevision)],
        ['sourceFinalized', val(ev.dateSourceFinalized)],
        ['capaTaken', yesNo(ev.capaOpened)],
        ['dcfCreated', yesNo(ev.dcfCreated)],
        ['isClosed', yesNo(ev.isClosed)],
        ['capaStart', val(ev.capaStartDate)],
        ['capaClose', val(ev.capaClosedDate)],
        ['targetAchieved', yesNo(ev.targetAchieved)],
        ['severity', val(ev.severity)],
      ],
    });
  }
  if (!isHastane && !isHome) {
    sections.push({
      key: 'basic',
      rows: [
        ['date', val(visit.date)],
        ['visitType', visitTypeLabel],
        ['startTime', val(visit.startTime)],
        ['endTime', val(visit.endTime)],
        ['patientId', val(visit.patientId)],
        ['doctor', val(visit.doctor)],
        ['hospital', val(visit.hospital)],
        ['city', val(visit.city)],
        ['travelType', val(visit.travelType)],
        ['status', <Badge tone={visit.status ? 'success' : 'warning'}>{statusText}</Badge>],
        ['sourceDocument', val(visit.sourceDocument)],
        ['sourceDocumentTime', val(visit.sourceDocumentCompletionTime)],
        ['information', val(visit.information), true],
      ],
    });
  }
  const otherRows = [
    ['createdAt', visit.createdAt?.seconds ? new Date(visit.createdAt.seconds * 1000).toLocaleString(locale) : formatTimestamp(visit.createdAt)],
    ['lastUpdated', val(visit.lastUpdated)],
    ['completedAt', val(visit.completedAt)],
    ['firstVisitDate', val(visit.firstVisitDate)],
    ['employeeName', val(visit.employeeName)],
    ['selectedCra', val(craName)],
    ['period', val(visit.period)],
    ['option', val(visit.option)],
  ];

  // Ekranda vizit penceresindeki güncel soru metni gösterilir; yoksa eski Türkçe etiket
  const todoText = (key) => {
    const path = `visitModal.todo.items.${key}`;
    const translated = t(path);
    return translated !== path ? translated : (TODO_LABELS[key] || key);
  };
  const todoEntries = Object.entries(visit.todoList || {});
  const todoGroups = [
    { key: 'preVisit', items: todoEntries.filter(([key]) => key.startsWith('pre_')) },
    { key: 'duringVisit', items: todoEntries.filter(([key]) => !key.startsWith('pre_')) },
  ].filter((group) => group.items.length > 0);
  const todoDone = todoEntries.filter(([, value]) => value).length;

  const renderRows = (rows) => (
    <dl className="tw-grid tw-grid-cols-1 tw-gap-x-5 tw-gap-y-3 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4">
      {rows.map(([key, value, wide]) => (
        <div key={key} className={`tw-min-w-0 ${wide ? 'sm:tw-col-span-2' : ''}`}>
          <dt className="tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{label(`details.fields.${key}`)}</dt>
          <dd className={`tw-mt-0.5 tw-whitespace-pre-line tw-break-words tw-text-sm tw-font-medium ${value === '-' ? 'tw-text-slate-300 dark:tw-text-slate-600' : 'tw-text-slate-800 dark:tw-text-slate-100'}`}>
            {value ?? '-'}
          </dd>
        </div>
      ))}
    </dl>
  );

  return (
    <Modal
      isOpen={Boolean(show)}
      onClose={onHide}
      size="xl"
      title={label('details.title')}
      subtitle={[visitTypeLabel !== '-' ? visitTypeLabel : null, visit.date, visit.patientId].filter(Boolean).join(' · ')}
      closeLabel={t('common.close')}
      lang={language}
      icon={(
        <GlassIcon gradient="tw-from-sky-400 tw-to-indigo-600 tw-shadow-sky-500/30" size="xs">
          <LuClipboardList aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      footer={(
        <>
          <Button variant="ghost" onClick={onHide}>{label('close')}</Button>
          <Button variant="success" onClick={downloadExcel} className="max-sm:tw-flex-1">
            <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
            {label('details.excel')}
          </Button>
        </>
      )}
    >
      <div className="tw-space-y-4">
        {sections.map((section) => (
          <section key={section.key} className={SECTION}>
            <h3 className={BLOCK_TITLE}>{label(`details.sections.${section.key}`)}</h3>
            {renderRows(section.rows)}
          </section>
        ))}

        {todoEntries.length > 0 && (
          <section className={SECTION}>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
              <h3 className={BLOCK_TITLE}>{label('details.sections.todo')}</h3>
              <Badge tone={todoDone === todoEntries.length ? 'success' : 'warning'}>
                {label('details.todoCount', { done: todoDone, total: todoEntries.length })}
              </Badge>
            </div>
            {todoGroups.map((group) => (
              <div key={group.key} className="tw-space-y-2">
                <p className="tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">{label(`details.${group.key}`)}</p>
                <ul className="tw-grid tw-gap-2 md:tw-grid-cols-2">
                  {group.items.map(([key, value]) => (
                    <li key={key} className="tw-flex tw-items-start tw-gap-2.5 tw-rounded-xl tw-border tw-border-slate-200/80 tw-bg-white/70 tw-px-3 tw-py-2 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                      <span
                        className={`tw-mt-px tw-grid tw-h-5 tw-w-5 tw-shrink-0 tw-place-items-center tw-rounded-full ${value ? 'tw-bg-emerald-500 tw-text-white' : 'tw-bg-rose-500/15 tw-text-rose-600 dark:tw-text-rose-300'}`}
                        title={value ? label('details.done') : label('details.notDone')}
                      >
                        {value ? <LuCheck aria-hidden="true" className="tw-h-3 tw-w-3" strokeWidth={3} /> : <LuX aria-hidden="true" className="tw-h-3 tw-w-3" strokeWidth={3} />}
                        <span className="tw-sr-only">{value ? label('details.done') : label('details.notDone')}</span>
                      </span>
                      <span className="tw-min-w-0 tw-text-sm tw-leading-snug tw-text-slate-700 dark:tw-text-slate-200">{todoText(key)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </section>
        )}

        <section className={SECTION}>
          <h3 className={BLOCK_TITLE}>{label('details.sections.other')}</h3>
          {renderRows(otherRows)}
        </section>
      </div>
    </Modal>
  );
};

VisitDetailsDialog.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func.isRequired,
  visit: PropTypes.object.isRequired,
};

export default VisitDetailsDialog;
